import type { QueryClient } from '@tanstack/react-query';
import { clearLicenseRequired } from './licenseStatus';
import { clearTermsAcceptanceRequired } from './termsStatus';
import { LAST_ACTIVITY_KEY } from './idleSession';

/**
 * User-scoped client state (account deletion / logout / account switch).
 *
 * Everything the SPA keeps about the signed-in user lives in:
 *  - the in-memory React Query cache (aquariums, journal, livestock, … —
 *    staleTime 5 min, so a cached list is served without a refetch), and
 *  - localStorage (session tokens, selected/active aquarium ids).
 * None of it was keyed by user, so after "delete account" + a new sign-up in
 * the same tab the previous account's data was rendered from the cache.
 *
 * clearUserClientState() wipes all of it and keeps only non-personal device
 * preferences (theme, language). AuthProvider calls it on logout, on account
 * deletion and whenever a different user signs in (ensureCacheOwner).
 */

/** Non-personal device preferences that survive a sign-out. */
export const PRESERVED_STORAGE_KEYS: readonly string[] = ['reefguide-theme', 'reefguide.language'];

/** The Cognito sub the current client caches belong to. */
export const CACHE_OWNER_KEY = 'reefguide:cacheOwner';

const TOKENS_KEY = 'reefguide_tokens';

function clearStorage(storage: Storage, preserved: readonly string[]): void {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key !== null && !preserved.includes(key)) keys.push(key);
  }
  keys.forEach((key) => storage.removeItem(key));
}

/**
 * Drops every cached query/mutation. Inactive queries are removed; queries
 * that still have mounted observers (e.g. the root-level aquarium list) are
 * reset to their initial, data-less state so those observers stop rendering
 * the previous user's data and refetch with the current session. (clear()
 * alone is not enough: a mounted observer keeps showing the removed query's
 * data until it re-renders.)
 */
export function resetQueryCache(queryClient: QueryClient): void {
  void queryClient.cancelQueries().catch(() => undefined);
  queryClient.removeQueries({ type: 'inactive' });
  queryClient.getMutationCache().clear();
  void queryClient.resetQueries().catch(() => undefined);
}

export interface ClearUserClientStateOptions {
  /** Keep the session tokens and its idle clock (used right after a sign-in stored the new session). */
  keepSession?: boolean;
}

/** Removes every piece of user-scoped client state. Never throws. */
export function clearUserClientState(
  queryClient: QueryClient | null | undefined,
  options: ClearUserClientStateOptions = {},
): void {
  // Storage first: queries reset below refetch immediately and must not see
  // the previous user's session.
  try {
    clearStorage(
      localStorage,
      // A kept session keeps its idle clock too (lib/idleSession).
      options.keepSession ? [...PRESERVED_STORAGE_KEYS, TOKENS_KEY, LAST_ACTIVITY_KEY] : PRESERVED_STORAGE_KEYS,
    );
  } catch {
    // storage unavailable (private mode / blocked)
  }
  try {
    clearStorage(sessionStorage, []);
  } catch {
    // storage unavailable
  }
  try {
    if (queryClient) resetQueryCache(queryClient);
  } catch {
    // a broken cache must not block the sign-out
  }
  clearLicenseRequired();
  clearTermsAcceptanceRequired();
}

/**
 * Called after a successful sign-in / session restore, once the new session
 * is stored: when the caches belong to another (or an unknown) user, wipe
 * them — keeping the new session — before the new user's data loads.
 * Returns true when state was cleared.
 */
export function ensureCacheOwner(sub: string, queryClient: QueryClient | null | undefined): boolean {
  let owner: string | null = null;
  try {
    owner = localStorage.getItem(CACHE_OWNER_KEY);
  } catch {
    owner = null;
  }
  const cleared = owner !== sub;
  if (cleared) clearUserClientState(queryClient, { keepSession: true });
  try {
    localStorage.setItem(CACHE_OWNER_KEY, sub);
  } catch {
    // storage unavailable
  }
  return cleared;
}

/** The `sub` claim of a JWT, or '' when it cannot be decoded. */
export function decodeJwtSub(token: string): string {
  try {
    const part = token.split('.')[1];
    if (!part) return '';
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    return typeof payload['sub'] === 'string' ? payload['sub'] : '';
  } catch {
    return '';
  }
}

/** The Cognito sub of the session stored in localStorage, or ''. */
export function currentUserSub(): string {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return typeof parsed['idToken'] === 'string' ? decodeJwtSub(parsed['idToken']) : '';
  } catch {
    return '';
  }
}

/**
 * Per-user localStorage key `<base>:<sub>`, so one user's persisted selection
 * can never be read for another user on a shared device. Without a session
 * (signed-out pages, tests) the base key is used.
 */
export function userScopedStorageKey(base: string): string {
  const sub = currentUserSub();
  return sub ? `${base}:${sub}` : base;
}
